/**
 * 「要注意にする選択肢」を**画面の現在値**から判定する（#3358）。
 *
 * 保存済みの要注意（reviewIssues / result_data 由来）は OCR 時点のスナップショットなので、
 * 画面で値を変えても追従しない。計算フィールドは金額を直すと結果が変わり、
 * 読み取った選択肢は人が選び直すため、どちらも現在値で作り直す必要がある。
 *
 * セル下部の赤字とサマリーの両方がここを参照する。片方だけ現在値にすると
 * 「セルは不一致なのにサマリーに出ない」のような食い違いが起きる。
 *
 * ⚠️ 判定の条件は utils/pattern/reviewIssues.ts の reviewChoice と同じ
 *    （選ばれた値が reviewChoiceValues に含まれるか）。文言も揃えること。
 *    ズレると、保存前の表示と保存後の表示が食い違う。
 */
import { reviewIssueCode, reviewIssueMessage, reviewIssueSource } from '../../constants/enum';
import { findChoiceLabel } from './reviewIssues';
import type { ItemChoiceType } from '../../types/common';
import type { ReviewIssue } from '../../types/reviewIssue';

/** 判定に必要な項目の情報（単一項目・明細の列で共通） */
interface LiveReviewChoiceField {
  /** 項目ID */
  id: string;
  /** 計算フィールドかどうか */
  is_calculated?: boolean;
  /** 有効な項目かどうか */
  is_active?: boolean;
  /** 要注意にする選択肢の出力値 */
  reviewChoiceValues?: string[];
  /** 選択肢（表示名を引くのに使う） */
  choices?: ItemChoiceType[];
}

/** 判定に必要な項目の情報 */
export interface LiveReviewChoiceItem extends LiveReviewChoiceField {
  /** 表示用の項目名 */
  parentValue: string;
  /** 明細の列。明細でなければ undefined */
  children?: Array<LiveReviewChoiceField & { childValue: string }>;
}

/** 明細の行ごとの計算値（#3358） */
export interface LiveReviewChoiceRowValue {
  /** 親項目ID */
  parentId: string;
  /** 子項目ID */
  childId: string;
  /** 表示上の行 index */
  rowIndex: number;
  /** その行の計算値 */
  value: string;
}

/**
 * 要注意の対象になるか（共通の足切り）。
 *
 * 計算フィールドだけでなく、**AI が読み取った選択肢も対象にする**。
 * 人が選び直したときに要注意が外れないと、直したのに消えないように見えるため。
 */
const isLiveReviewTarget = (field: LiveReviewChoiceField): boolean =>
  field.is_active === true &&
  field.reviewChoiceValues !== undefined &&
  field.reviewChoiceValues.length > 0;

/**
 * 現在値で要注意になる計算フィールドを ReviewIssue の形で返す（#3358）。
 *
 * 単一項目と明細の列の両方を対象にする。明細は行ごとに計算値が決まるため、
 * 行の値（rowValues）を別に受け取る。
 *
 * @param items 項目一覧
 * @param calculatedValues 項目ID → 現在値（計算値、または人が選んだ値）
 * @param rowValues 明細の行ごとの現在値
 */
export const buildLiveReviewChoiceIssues = (
  items: LiveReviewChoiceItem[],
  calculatedValues: Map<string, string>,
  rowValues: LiveReviewChoiceRowValue[] = [],
): ReviewIssue[] => {
  const issues: ReviewIssue[] = [];

  items.forEach(item => {
    if (!isLiveReviewTarget(item)) return;

    const value = calculatedValues.get(item.id);
    if (value === undefined || !item.reviewChoiceValues?.includes(value)) return;

    issues.push({
      // 単一項目のパスは項目ID（evaluateReviewIssues と同じ形）
      path: item.id,
      item_id: item.id,
      label: item.parentValue,
      source: reviewIssueSource.validation,
      code: reviewIssueCode.reviewChoice,
      // 出力値ではなく表示名を出す。CSV 用のコード値（"9"）では人が読めないため
      message: reviewIssueMessage.reviewChoice(findChoiceLabel(item.choices, value)),
    });
  });

  // 明細の列（#3358）。行ごとに値が違うため、行の数だけ判定する
  const childById = new Map<string, LiveReviewChoiceField & { childValue: string }>();
  items.forEach(item => {
    item.children?.forEach(child => childById.set(child.id, child));
  });

  rowValues.forEach(row => {
    const child = childById.get(row.childId);
    if (child === undefined || !isLiveReviewTarget(child)) return;
    if (!child.reviewChoiceValues?.includes(row.value)) return;

    issues.push({
      // 明細セルのパスは `親ID[行index].子ID`（evaluateReviewIssues と同じ形）
      path: `${row.parentId}[${row.rowIndex}].${row.childId}`,
      item_id: row.childId,
      row_index: row.rowIndex,
      label: child.childValue,
      source: reviewIssueSource.validation,
      code: reviewIssueCode.reviewChoice,
      message: reviewIssueMessage.reviewChoice(findChoiceLabel(child.choices, row.value)),
    });
  });

  return issues;
};

/**
 * 保存済みの要注意から、計算フィールドの reviewChoice だけを取り除く（#3358）。
 *
 * 取り除いた分は buildLiveReviewChoiceIssues が現在値で作り直す。
 * 両方残すと、値を直した後も古い判定がサマリーに残って消えない。
 */
export const withoutStaleReviewChoices = (
  issues: ReviewIssue[],
  items: LiveReviewChoiceItem[],
): ReviewIssue[] => {
  const liveItemIds = new Set<string>();
  items.forEach(item => {
    if (isLiveReviewTarget(item)) liveItemIds.add(item.id);
    // 明細の列も現在値で作り直すため、保存済みの判定は落とす
    item.children?.forEach(child => {
      if (isLiveReviewTarget(child)) liveItemIds.add(child.id);
    });
  });
  if (liveItemIds.size === 0) return issues;

  return issues.filter(
    issue => !(issue.code === reviewIssueCode.reviewChoice && liveItemIds.has(issue.item_id)),
  );
};
